import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ChevronDownSmall } from '@peacecover/icon';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { useGetBettieEnergies } from '../../domains/bettie/ui/hooks/useGetBettieEnergies';
import StickyHeader from '../../components/StickyHeader';
import EnergyHeart from './components/EnergyHeart';
import FreeHeartChargeCard from './components/FreeHeartChargeCard';
import FreeEnergyChargeCard from './components/FreeEnergyChargeCard';
import { ReactComponent as GradientHeartIcon } from '../../icons/likeGradientShape.svg';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';

const FREE_CHARGE_ENERGY_LIST = [
  {
    id: 1,
    title: t('BettieFreeCharge.freeChargeEnergy.attendance'),
    description: t('BettieFreeCharge.freeChargeEnergy.onceADay'),
    earned: t('BettieFreeCharge.freeChargeEnergy.heartPlusEnergy', { heart: 1, energy: 1 }),
    type: 'attendance' as const
  },
  {
    id: 2,
    title: t('BettieFreeCharge.freeChargeEnergy.writeInLounge'),
    description: t('BettieFreeCharge.freeChargeEnergy.onceADay'),
    earned: t('BettieFreeCharge.freeChargeEnergy.energyCount', { count: 1 }),
    type: 'lounge' as const
  },
  {
    id: 3,
    title: t('BettieFreeCharge.freeChargeEnergy.showAd'),
    description: t('BettieFreeCharge.freeChargeEnergy.unlimited'),
    earned: t('BettieFreeCharge.freeChargeEnergy.energyCount', { count: 1 }),
    type: 'ads' as const
  },
  {
    id: 4,
    title: t('BettieFreeCharge.freeChargeEnergy.findBuddy'),
    description: t('BettieFreeCharge.freeChargeEnergy.timesPerDay', { count: 10 }),
    earned: t('BettieFreeCharge.freeChargeEnergy.readyForOpen'),
    type: 'findBuddy' as const
  }
];

const FREE_CHARGE_HEART_LIST = [
  {
    id: 1,
    title: t('BettieFreeCharge.freeChargeHeart.inviteFriends'),
    description: 'BettieFreeCharge.freeChargeHeart.unlimited',
    icon: '/assets/icons/graphic-3d/3D.Sparkling heart.png',
    type: 'invite' as const
  },
  {
    id: 2,
    title: t('BettieFreeCharge.freeChargeHeart.goToAdpopcorn'),
    description: 'BettieFreeCharge.freeChargeHeart.maxHeartCount',
    icon: '/assets/icons/graphic-3d/3D.gift.png',
    type: 'adpopcorn' as const
  }
];

const FreeChargePage = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: bettieEnergies, isLoading: isBettieEnergiesLoading } = useGetBettieEnergies();

  const navigate = useNavigate();

  const { data: bettieBalances, isLoading } = useGetBettieBalances();
  const totalHeartCount = bettieBalances?.bettiePoint?.balance ?? 0;

  const visibleEnergyList = isExpanded
    ? FREE_CHARGE_ENERGY_LIST
    : FREE_CHARGE_ENERGY_LIST.slice(0, 2);

  const expandedSectionStyle = isExpanded
    ? ''
    : 'bg-[linear-gradient(176deg,rgba(255,255,255,0.00)_10.6%,#FFF_71.21%,#FFF_105.55%)] dark:bg-[linear-gradient(0deg,#151619_36.5%,rgba(21,22,25,0.80)_57.48%,rgba(21,22,25,0.00)_100%)] dark:shadow-[0_-7px_13.2px_0_rgba(255,255,255,0.03)_inset] mt-[-90px] h-[100px]';

  if (isBettieEnergiesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div className="pt-[env(safe-area-inset-top)]">
      <StickyHeader
        showBackButton={true}
        rightContent={
          <div className="flex items-center gap-[2px]" data-fly-target="free-charge-total-heart">
            <GradientHeartIcon width={20} height={20} />
            <p className="text-[16px] font-medium text-text-main-lv1 leading-[19px]">
              {isLoading ? '-' : `${totalHeartCount}개`}
            </p>
          </div>
        }
      />
      <div className="flex flex-col mt-[64px]">
        <div className="bg-background-light dark:bg-background-dark shadow-[0_4px_10px_0_rgba(0,0,0,0.04),_0_0_24px_0_rgba(255,255,255,0.30)_inset] dark:shadow-[0_4px_10px_0_rgba(255,255,255,0.04),0_0_24px_0_rgba(0,0,0,0.30)_inset] backdrop:filter-blur-[20px] rounded-b-[20px]">
          <div className="flex flex-col items-start gap-[9px] px-[24px]">
            <h1 className="text-[26px] font-bold text-text-main-lv2 leading-[31px]">
              {t('BettieFreeCharge.title')}
            </h1>
            <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px]">
              {t('BettieFreeCharge.description')}
            </p>
          </div>
          <div className="mt-[26px] flex justify-center">
            <EnergyHeart
              count={bettieEnergies?.totalEnergy ?? 0}
              energyPerPoint={bettieEnergies?.energyPerPoint ?? 5}
            />
          </div>
          <div className="pt-[28px] flex flex-col gap-[12px] rounded-b-[20px] px-[24px]">
            {visibleEnergyList.map((item) => (
              <FreeEnergyChargeCard
                key={item.id}
                item={item}
                lastDailyCheckInRewardedAt={bettieEnergies?.lastDailyCheckInRewardedAt}
                lastAdRewardedAt={bettieEnergies?.lastAdRewardedAt}
                lastPostRewardedAt={bettieEnergies?.lastPostRewardedAt}
              />
            ))}
            <div
              className={`flex items-end justify-center mx-[-24px] z-[1] rounded-b-[20px] pb-[12px] ${expandedSectionStyle}`}
            >
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                type="button"
                className="flex items-center gap-[2px] justify-center py-[8px]"
              >
                <p className="text-[13px] text-text-main-lv2 leading-[16px]">
                  {isExpanded ? t('BettieFreeCharge.collapse') : t('BettieFreeCharge.loadMore')}
                </p>
                <ChevronDownSmall
                  size={12}
                  className={`text-text-main-lv2 ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="pb-[calc(env(safe-area-inset-bottom)+80px)] flex flex-col items-center">
          <div className="mt-[30px] px-[24px]">
            <p className="text-[16px] font-semibold text-text-main-lv2 leading-[19px]">
              {t('BettieFreeCharge.heartChargeTitle')}
            </p>
            <div className="flex items-center gap-[10px] mt-[18px]">
              {FREE_CHARGE_HEART_LIST.map((item) => (
                <FreeHeartChargeCard key={item.id} item={item} />
              ))}
            </div>
          </div>
          <button
            type="button"
            className="flex items-center justify-center gap-[5px] mt-[25px]"
            onClick={() => navigate('/free-charge/enter-redeem-code')}
          >
            <p className="text-[13px] text-text-main-lv4 leading-[16px]">
              {t('BettieFreeCharge.haveAnyCode')}
            </p>
            <p
              className="text-[13px] text-text-main-lv2 leading-[16px]"
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: '2px'
              }}
            >
              {t('BettieFreeCharge.enterCode')}
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeChargePage;
